<div class="surface-card p-4 sm:p-6">
  <!-- Header -->
  <div class="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h2 class="mb-1 text-xl font-semibold text-text-primary">{{ 'seekerDashboard.comparison.title' | translate }}</h2>
      <p class="text-sm text-text-secondary">
        @if (!showComparison) {
        {{ 'seekerDashboard.comparison.subtitleSelect' | translate }} {{ MAX }} {{
        'seekerDashboard.comparison.subtitleSelectSuffix' | translate }}
        } @else {
        {{ 'seekerDashboard.comparison.subtitleShowing' | translate }} {{ comparisonData.length }} {{
        'seekerDashboard.comparison.subtitleShowingSuffix' | translate }}
        }
      </p>
    </div>
    <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-surface-elevated text-primary border border-border">
      <i class="fa-solid fa-chart-line text-xl"></i>
    </div>
  </div>

  <!-- Error -->
  @if (errorMessage) {
  <div class="alert alert-danger mx-auto mb-4 max-w-xl rounded-xl shadow-elevated">
    <i class="fas fa-exclamation-triangle mt-0.5"></i>
    <div>
      <p class="font-semibold text-text-primary">Unable to load comparison data</p>
      <p class="mt-1 text-sm text-text-secondary">{{ errorMessage }}</p>
    </div>
  </div>
  }

  @if (!showComparison) {

  <!-- Loading -->
  @if (isLoadingFavorites) {
  <div class="rounded-xl border border-border bg-surface shadow-elevated overflow-hidden">
    <div class="border-b border-border-strong bg-surface-elevated px-4 py-4">
      <div class="flex items-center gap-3">
        <span class="spinner"></span>
        <span class="text-sm font-medium text-text-secondary">{{ 'seekerDashboard.comparison.loadingFavorites' | translate }}</span>
      </div>
    </div>
    <div class="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
      @for (item of [1, 2, 3]; track item) {
      <div class="rounded-xl border border-border bg-surface-elevated p-3">
        <div class="skeleton mb-4 h-32 rounded-lg"></div>
        <div class="skeleton mb-2 h-4 w-2/3"></div>
        <div class="skeleton h-3 w-1/2"></div>
      </div>
      }
    </div>
  </div>

  <!-- Empty -->
  } @else if (favorites.length === 0) {
  <div class="rounded-xl border border-border bg-surface p-10 text-center shadow-elevated">
    <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-border-strong bg-surface-elevated text-text-muted">
      <i class="fa-regular fa-heart text-2xl"></i>
    </div>
    <h3 class="text-lg font-semibold text-text-primary">{{ 'seekerDashboard.comparison.noFavorites' | translate }}</h3>
    <p class="mx-auto mt-2 max-w-sm text-sm text-text-secondary">
      {{ 'seekerDashboard.comparison.subtitleSelect' | translate }} {{ MAX }}
      {{ 'seekerDashboard.comparison.subtitleSelectSuffix' | translate }}
    </p>
  </div>

  <!-- List -->
  } @else {

  <!-- Action bar -->
  <div class="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border-strong bg-surface-elevated px-4 py-3 shadow-subtle">
    <span class="text-sm text-text-secondary">
      @if (selected.length === 0) {
      {{ 'seekerDashboard.comparison.selectAtLeast2' | translate }}
      } @else {
      <strong class="text-primary">{{ selected.length }}</strong>/{{ MAX }}
      {{ 'seekerDashboard.comparison.selectedCount' | translate }}
      &nbsp;&middot;&nbsp; {{ 'seekerDashboard.comparison.priceType' | translate }}
      <strong class="capitalize text-text-primary">{{ activePriceType }}</strong>
      }
    </span>
    <div class="flex gap-2">
      @if (selected.length > 0) {
      <button (click)="clear()" class="btn btn-secondary px-3 py-1.5 text-sm">
        {{ 'seekerDashboard.comparison.clearButton' | translate }}
      </button>
      }
      <button (click)="compare()" [disabled]="selected.length < 2 || isLoadingComparison" class="btn btn-primary px-4 py-1.5 text-sm font-medium flex items-center gap-2">
        @if (isLoadingComparison) {
        <i class="fas fa-spinner fa-spin"></i>
        } @else {
        <i class="fa-solid fa-scale-balanced"></i>
        }
        {{ 'seekerDashboard.comparison.compareButton' | translate }}
      </button>
    </div>
  </div>

  <!-- Favorites grid -->
  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
    @for (item of favorites; track item.itemId) {
    <div (click)="toggle(item)" class="relative overflow-hidden rounded-xl border bg-surface shadow-subtle transition-all duration-200"
      [class.border-primary]="isSelected(item)" [class.shadow-glow]="isSelected(item)"
      [class.border-border]="!isSelected(item)"
      [class.hover:border-primary/50]="canSelect(item)" [class.opacity-40]="!canSelect(item)"
      [class.cursor-pointer]="canSelect(item)" [class.cursor-not-allowed]="!canSelect(item)">

      <!-- Check badge -->
      @if (isSelected(item)) {
      <div class="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-primary/30 bg-primary text-white shadow-glow">
        <i class="fa-solid fa-check text-xs"></i>
      </div>
      }

      <!-- Image -->
      <div class="relative h-40 overflow-hidden bg-surface-elevated">
        <img [src]="item.image" [alt]="item.title" class="h-full w-full object-cover transition duration-500 hover:scale-105"
          onerror="this.src='assets/placeholder.jpg'" />
        <span class="absolute bottom-2 left-2 badge text-xs font-semibold capitalize"
          [class.badge-success]="item.priceType === 'sale'"
          [class.badge-primary]="item.priceType !== 'sale'">
          {{ item.priceType }}
        </span>
      </div>

      <!-- Info -->
      <div class="p-3">
        <h3 class="mb-1 truncate text-sm font-semibold text-text-primary">{{ item.title }}</h3>
        <p class="mb-2 truncate text-xs text-text-muted">
          <i class="fa-solid fa-location-dot mr-1"></i>{{ item.location }}
        </p>
        @if (item.price) {
        <p class="text-sm font-bold text-primary">
          {{ item.price | number }} {{ item.currency }}
        </p>
        }
        <div class="mt-2 flex gap-3 text-xs text-text-muted font-mono tracking-wider">
          @if (item.bedrooms) { <span><i class="fa-solid fa-bed mr-1"></i>{{ item.bedrooms }}</span> }
          @if (item.bathrooms) { <span><i class="fa-solid fa-bath mr-1"></i>{{ item.bathrooms }}</span> }
          @if (item.area) { <span><i class="fa-solid fa-ruler-combined mr-1"></i>{{ item.area }} m2</span> }
        </div>
      </div>
    </div>
    }
  </div>
  }
  }

  @if (showComparison) {
  <button (click)="back()" class="btn btn-secondary mb-4 flex items-center gap-2 px-4 py-2 text-sm">
    <i class="fa-solid fa-arrow-left"></i> {{ 'seekerDashboard.comparison.backToSelection' | translate }}
  </button>

  <div class="rounded-xl border border-border bg-surface shadow-elevated overflow-hidden">
    <div class="flex flex-col gap-3 border-b border-border-strong bg-surface-elevated px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p class="text-sm font-semibold text-text-primary">{{ 'seekerDashboard.comparison.title' | translate }}</p>
        <p class="mt-1 text-xs text-text-muted">{{ comparisonData.length }} {{ 'seekerDashboard.comparison.subtitleShowingSuffix' | translate }}</p>
      </div>
      <span class="inline-flex w-max items-center gap-2 rounded-full border border-border-strong bg-surface px-3 py-1.5 text-xs font-medium capitalize text-text-secondary">
        <span class="h-1.5 w-1.5 rounded-full bg-primary shadow-glow"></span>
        {{ activePriceType || 'comparison' }}
      </span>
    </div>

    <div class="overflow-x-auto">
      <table class="table-premium w-full min-w-[720px]">
        <thead>
          <tr>
            <th class="sticky left-0 z-10 w-40 bg-surface-elevated uppercase tracking-wider">
              {{ 'seekerDashboard.comparison.title' | translate }}
            </th>
            @for (unit of comparisonData; track unit.id) {
            <th class="min-w-48 text-center align-top">
              <img [src]="unit.image" [alt]="unit.title" class="mx-auto mb-3 h-28 w-full max-w-[200px] rounded-lg object-cover border border-border shadow-subtle" onerror="this.src='assets/placeholder.jpg'" />
              <p class="font-semibold leading-snug text-text-primary">{{ unit.title }}</p>
              <p class="mt-1 text-xs text-text-muted">{{ unit.location }}</p>
            </th>
            }
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-tag mr-2 text-primary"></i>{{ 'seekerDashboard.comparison.tablePrice' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center">
              @if (unit.price && unit.price.value) {
              <span class="font-bold text-primary">{{ unit.price.value | number }}</span>
              <span class="ml-1 text-xs text-text-muted">EGP</span>
              <div class="text-xs text-text-muted mt-1">{{ unit.price.label }}</div>
              } @else { <span class="text-text-muted">-</span> }
            </td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-star mr-2 text-warning"></i>{{ 'seekerDashboard.comparison.tableRating' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center">
              <span class="font-semibold text-text-primary">{{ unit.rating.value }}</span>
              <span class="ml-1 text-xs text-text-muted">({{ unit.rating.count }})</span>
            </td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-bed mr-2 text-info"></i>{{ 'seekerDashboard.comparison.tableBedrooms' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center font-semibold text-text-primary">{{ unit.specs.bedrooms }}</td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-bath mr-2 text-info"></i>{{ 'seekerDashboard.comparison.tableBathrooms' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center font-semibold text-text-primary">{{ unit.specs.bathrooms }}</td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-ruler-combined mr-2 text-success"></i>{{ 'seekerDashboard.comparison.tableArea' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center font-semibold text-text-primary">{{ unit.specs.area }} m2</td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-building mr-2 text-text-muted"></i>{{ 'seekerDashboard.comparison.tableFloor' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center font-semibold text-text-primary">{{ unit.specs.floor }}</td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-couch mr-2 text-primary"></i>{{ 'seekerDashboard.comparison.tableFurnished' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center">
              <span class="badge text-xs font-medium" [class.badge-success]="unit.status.furnished" [class.badge-neutral]="!unit.status.furnished">
                {{ unit.status.text }}
              </span>
            </td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-calendar-check mr-2 text-emerald-400"></i>{{ 'seekerDashboard.comparison.tableAvailability' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center py-3">
              @let avail = getAvailabilityInfo(unit.status?.availability);

              @if (avail.type === 'range') {
                <div class="inline-flex flex-col items-center gap-1.5 py-1">
                  <!-- Date box -->
                  <div class="rounded-xl bg-surface-elevated/90 px-3 py-2 border border-border-strong shadow-subtle text-center">
                    <div class="flex items-center justify-center gap-1.5 text-xs text-text-primary mb-1.5">
                      <i class="fa-regular fa-calendar-days text-primary text-xs"></i>
                      <span class="text-[10.5px] text-text-muted font-medium">{{ isArabic ? 'فترة التوفر' : 'Availability' }}</span>
                    </div>
                    <div class="flex flex-col items-center gap-1 text-[11px] font-semibold text-text-primary leading-tight">
                      <span>{{ isArabic ? 'من: ' : 'From: ' }}<strong class="text-primary font-bold">{{ avail.startDate }}</strong></span>
                      <span>{{ isArabic ? 'إلى: ' : 'To: ' }}<strong class="text-primary font-bold">{{ avail.endDate }}</strong></span>
                    </div>
                  </div>

                  <!-- Status tag & duration -->
                  <div class="flex items-center justify-center gap-1.5 flex-wrap">
                    <span class="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 text-[10.5px] font-bold text-emerald-400">
                      <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                      {{ avail.label }}
                    </span>
                    @if (avail.duration) {
                      <span class="text-[10px] font-medium text-text-muted bg-surface-elevated px-2 py-0.5 rounded-full border border-border-subtle font-mono">
                        {{ avail.duration }}
                      </span>
                    }
                  </div>
                </div>
              } @else if (avail.type === 'now') {
                <div class="inline-flex flex-col items-center justify-center py-1">
                  <span class="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-3.5 py-1.5 text-xs font-bold text-emerald-400 shadow-subtle">
                    <span class="relative flex h-2 w-2">
                      <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span>{{ avail.label }}</span>
                  </span>
                </div>
              } @else if (avail.type === 'single') {
                <div class="inline-flex flex-col items-center justify-center py-1">
                  <div class="inline-flex items-center gap-1.5 rounded-xl bg-surface-elevated border border-border-strong px-3 py-1.5 text-xs font-medium shadow-subtle">
                    <i class="fa-regular fa-calendar-days text-primary text-xs"></i>
                    <span class="text-text-primary text-[11px]">{{ avail.label }} <strong class="text-primary">{{ avail.startDate }}</strong></span>
                  </div>
                </div>
              } @else if (avail.type === 'unavailable') {
                <div class="inline-flex flex-col items-center justify-center py-1">
                  <span class="inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 border border-rose-500/25 px-3 py-1 text-xs font-semibold text-rose-400 shadow-subtle">
                    <span class="h-1.5 w-1.5 rounded-full bg-rose-500"></span>
                    <span>{{ avail.label }}</span>
                  </span>
                </div>
              } @else if (avail.type === 'text') {
                <span class="badge badge-neutral px-2.5 py-1 text-xs font-medium">
                  {{ avail.label }}
                </span>
              } @else {
                <span class="text-text-muted">-</span>
              }
            </td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-list-check mr-2 text-primary"></i>{{ 'seekerDashboard.comparison.tableFeatures' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center">
              @if (unit.features.length > 0) {
              <div class="flex flex-wrap justify-center gap-1">
                @for (f of unit.features; track f.name) {
                <span class="badge badge-primary px-2 py-0.5 text-xs shadow-none">{{ f.name }}</span>
                }
              </div>
              } @else { <span class="text-text-muted">-</span> }
            </td>
            }
          </tr>
          <tr>
            <td class="sticky left-0 z-10 bg-surface font-semibold text-text-secondary">
              <i class="fa-solid fa-calendar mr-2 text-text-muted"></i>{{ 'seekerDashboard.comparison.tableYearBuilt' | translate }}
            </td>
            @for (unit of comparisonData; track unit.id) {
            <td class="text-center font-semibold text-text-primary">
              {{ unit.yearBuilt ?? '-' }}
            </td>
            }
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  }
</div>
